import React, { useEffect, useState } from 'react';
import { ArrowRight, Building2, CalendarDays, Globe2, LockKeyhole, Mail, MapPin, Phone, Plus, Search, ShieldCheck, Sparkles, Star, Trash2, User, UserPlus, Users, X, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import http from '../config/http';

const steps = [
  { title: 'Business profile', description: 'Add the contact details customers will see.', icon: Building2 },
  { title: 'Venues', description: 'Add the locations where you host events.', icon: MapPin },
  { title: 'Event types', description: 'Choose the kinds of events your team manages.', icon: CalendarDays },
  { title: 'Team members', description: 'Add users and assign their access roles.', icon: UserPlus },
];

const defaultEventTypes = ['Boda', 'Bautizo', 'Cumpleaños', 'Quinceañera'];

const stepBenefits = [
  { heading: 'Make a great first impression', points: [
    { icon: Star, title: 'Build trust', text: 'Show professional and accurate information to your customers.' },
    { icon: Search, title: 'Be more discoverable', text: 'Help people find your venue online.' },
    { icon: Users, title: 'Make it easy to get in touch', text: 'Share your phone and website so customers can reach you.' },
  ] },
  { heading: 'Keep your spaces organized', points: [
    { icon: MapPin, title: 'Manage all your venues', text: 'Add and organize the locations where you host events.' },
    { icon: Zap, title: 'Faster booking', text: 'Save venues now to speed up event creation later.' },
    { icon: CalendarDays, title: 'Better event planning', text: 'Keep your spaces in one place for a smoother workflow.' },
  ] },
  { heading: 'Capture your specialties', points: [
    { icon: Sparkles, title: 'Stay organized', text: 'Categorize your events to keep everything in one place.' },
    { icon: Zap, title: 'Set up faster', text: 'Save time with predefined event types.' },
    { icon: Users, title: 'Clearer event workflows', text: 'Help your team manage each type of event efficiently.' },
  ] },
  { heading: 'Work better together', points: [
    { icon: ShieldCheck, title: 'Collaborate securely', text: 'Give the right access role to everyone on your team.' },
    { icon: Zap, title: 'Get started faster', text: 'Invite your team now and hit the ground running.' },
    { icon: LockKeyhole, title: 'Keep control', text: 'You can manage permissions anytime.' },
  ] },
];

function ListEditor({ values, onChange, placeholder, buttonLabel, rows = false }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const value = draft.trim();
    if (!value || values.some(item => item.toLowerCase() === value.toLowerCase())) return;
    onChange([...values, value]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex gap-3">
        <input
          value={draft}
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
          maxLength={100}
          placeholder={placeholder}
          className="h-14 flex-1 rounded-xl border border-slate-300 px-4 text-base outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
        />
        <button type="button" onClick={add} className="flex h-14 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-700 to-purple-600 px-6 text-sm font-semibold text-white shadow-sm hover:from-violet-800 hover:to-purple-700">
          <Plus className="h-4 w-4" /> {buttonLabel || 'Add'}
        </button>
      </div>
      <div className={`mt-5 min-h-10 ${rows ? 'space-y-3' : 'flex flex-wrap gap-2'}`}>
        {values.map(value => rows ? (
          <div key={value} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-50 text-violet-700"><MapPin className="h-5 w-5" /></span><span className="font-semibold text-slate-900">{value}</span></div>
            <button type="button" onClick={() => onChange(values.filter(item => item !== value))} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${value}`}><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <span key={value} className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-4 py-2 text-sm font-medium text-violet-800">
            {value}<button type="button" onClick={() => onChange(values.filter(item => item !== value))} aria-label={`Remove ${value}`}><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { settings, updateSetting, loading } = useSettings();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [userDraft, setUserDraft] = useState({ name: '', email: '', role: '' });
  const [form, setForm] = useState({
    name: '', phone: '', websiteUrl: '',
    venueNames: [], eventTypes: defaultEventTypes,
  });

  useEffect(() => {
    if (!settings?._id) return;
    if (settings.onboardingStatus === 'completed') {
      navigate('/settings', { replace: true });
      return;
    }
    setStep(Math.min(Number(settings.onboardingStep) || 0, steps.length - 1));
    setForm({
      name: settings.name || '',
      phone: settings.phone || '',
      websiteUrl: settings.websiteUrl || '',
      venueNames: Array.isArray(settings.venueNames) ? settings.venueNames : [],
      eventTypes: Array.isArray(settings.eventTypes) && settings.eventTypes.length ? settings.eventTypes : defaultEventTypes,
    });
  }, [settings, navigate]);

  useEffect(() => {
    if (step !== 3 || roles.length) return;
    http.get('/api/settings/roles')
      .then(({ data }) => {
        setRoles(data);
        const defaultRole = data.find(role => role.name?.toLowerCase() === 'sales') || data[0];
        if (defaultRole) setUserDraft(previous => ({ ...previous, role: defaultRole._id }));
      })
      .catch(() => setError('Unable to load roles. You can skip this step and add users later in Settings.'));
  }, [step, roles.length]);

  const generateTemporaryPassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    const bytes = new Uint32Array(14);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, value => alphabet[value % alphabet.length]).join('');
  };

  const addUser = async () => {
    if (!userDraft.name.trim() || !userDraft.email.trim() || !userDraft.role) {
      setError('Name, email, and role are required.');
      return;
    }
    setAddingUser(true);
    setError('');
    const temporaryPassword = generateTemporaryPassword();
    try {
      const { data } = await http.post('/api/users', {
        name: userDraft.name.trim(),
        email: userDraft.email.trim().toLowerCase(),
        role: userDraft.role,
        status: 'Active',
        password: temporaryPassword,
      });
      const roleName = roles.find(role => role._id === userDraft.role)?.name || 'User';
      setAddedUsers(previous => [...previous, { ...data, roleName, temporaryPassword }]);
      setUserDraft({ name: '', email: '', role: userDraft.role });
    } catch (requestError) {
      setError(requestError.userMessage || requestError.response?.data?.error || 'Unable to add this user.');
    } finally {
      setAddingUser(false);
    }
  };

  const finishOrAdvance = async (updates = {}, skipped = false) => {
    setSaving(true);
    setError('');
    const isLast = step === steps.length - 1;
    const skippedSteps = [...new Set([...(settings.onboardingSkippedSteps || []), ...(skipped ? [step] : [])])];
    try {
      await updateSetting({
        ...updates,
        onboardingStep: isLast ? steps.length : step + 1,
        onboardingSkippedSteps: skippedSteps,
        onboardingStatus: isLast ? 'completed' : 'incomplete',
        ...(isLast ? { onboardingCompletedAt: new Date().toISOString() } : {}),
      });
      if (isLast) navigate('/', { replace: true });
      else setStep(current => current + 1);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to save this step. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveCurrentStep = () => {
    if (step === 0) return finishOrAdvance({ name: form.name.trim(), phone: form.phone.trim(), websiteUrl: form.websiteUrl.trim() });
    if (step === 1) return finishOrAdvance({ venueNames: form.venueNames });
    if (step === 2) return finishOrAdvance({ eventTypes: form.eventTypes });
    return finishOrAdvance();
  };

  const removeAddedUser = async userId => {
    try {
      await http.delete(`/api/users/${userId}`);
      setAddedUsers(previous => previous.filter(user => user._id !== userId));
    } catch (requestError) {
      setError(requestError.userMessage || 'Unable to remove this user.');
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#f8f8ff]"><div className="h-9 w-9 animate-spin rounded-full border-4 border-purple-200 border-t-purple-700" /></div>;

  const current = steps[step];
  const StepIcon = current.icon;
  const benefits = stepBenefits[step];
  const progress = ((step + 1) / steps.length) * 100;
  const inputClass = 'h-14 w-full rounded-xl border border-slate-300 bg-white pl-12 pr-4 text-base text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f3f0ff_0,_#fafbff_38%,_#f7f8fc_100%)]">
      <header className="flex h-[72px] items-center justify-between border-b border-slate-200 bg-white/90 px-7 backdrop-blur">
        <div className="text-lg font-bold text-slate-950">Venue Management setup</div>
        <button type="button" onClick={() => navigate('/')} className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">Exit</button>
      </header>

      <main className="mx-auto max-w-[1320px] px-6 py-10">
        <div className="mx-auto mb-9 max-w-[1140px]">
          <div className="mb-3 flex items-center gap-6 text-sm font-medium text-slate-500"><span className="shrink-0">Step {step + 1} of {steps.length}</span><div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-gradient-to-r from-violet-600 to-purple-500 transition-all duration-300" style={{ width: `${progress}%` }} /></div><span className="w-12 text-right">{Math.round(progress)}%</span></div>
        </div>

        <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(62,35,120,0.08)]">
          <div className="grid min-h-[610px] lg:grid-cols-[minmax(0,1.9fr)_minmax(330px,1fr)]">
            <div className="flex min-w-0 flex-col p-7 sm:p-10 lg:p-12">
              <div className="mb-9 flex items-start gap-5">
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700"><StepIcon className="h-8 w-8" /></span>
                <div><h1 className="text-3xl font-bold tracking-tight text-slate-950">{step === 3 ? 'Invite your team' : current.title}</h1><p className="mt-1.5 max-w-2xl text-base text-slate-500">{step === 3 ? 'Add team members and assign their access roles. You can always invite more people later.' : current.description}</p></div>
              </div>

              <div className="flex-1">
                {step === 0 && <div className="grid gap-5 sm:grid-cols-2">
                  <label className="sm:col-span-2"><span className="mb-2 block text-sm font-semibold text-slate-800">Business name</span><div className="relative"><Building2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input value={form.name} onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))} className={inputClass} /></div></label>
                  <label><span className="mb-2 block text-sm font-semibold text-slate-800">Phone</span><div className="relative"><Phone className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input value={form.phone} onChange={event => setForm(previous => ({ ...previous, phone: event.target.value }))} className={inputClass} /></div></label>
                  <label><span className="mb-2 block text-sm font-semibold text-slate-800">Website</span><div className="relative"><Globe2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input value={form.websiteUrl} placeholder="https://" onChange={event => setForm(previous => ({ ...previous, websiteUrl: event.target.value }))} className={inputClass} /></div></label>
                </div>}

                {step === 1 && <div><ListEditor rows values={form.venueNames} onChange={venueNames => setForm(previous => ({ ...previous, venueNames }))} placeholder="Venue name" buttonLabel="Add venue" /></div>}
                {step === 2 && <div><ListEditor values={form.eventTypes} onChange={eventTypes => setForm(previous => ({ ...previous, eventTypes }))} placeholder="Event type" buttonLabel="Add type" /></div>}

                {step === 3 && <div className="space-y-7">
                  <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-5">
                    <div className="grid gap-4 md:grid-cols-3">
                      <label><span className="mb-2 block text-sm font-semibold text-slate-800">Name</span><div className="relative"><User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input value={userDraft.name} placeholder="Enter full name" onChange={event => setUserDraft(previous => ({ ...previous, name: event.target.value }))} className={inputClass} /></div></label>
                      <label><span className="mb-2 block text-sm font-semibold text-slate-800">Email</span><div className="relative"><Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input type="email" value={userDraft.email} placeholder="name@company.com" onChange={event => setUserDraft(previous => ({ ...previous, email: event.target.value }))} className={inputClass} /></div></label>
                      <label><span className="mb-2 block text-sm font-semibold text-slate-800">Role</span><select value={userDraft.role} onChange={event => setUserDraft(previous => ({ ...previous, role: event.target.value }))} className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"><option value="">Select role</option>{roles.map(role => <option key={role._id} value={role._id}>{role.name}</option>)}</select></label>
                    </div>
                    <div className="mt-5 flex justify-end"><button type="button" onClick={addUser} disabled={addingUser || !roles.length} className="flex h-12 items-center gap-2 rounded-xl bg-gradient-to-r from-violet-700 to-purple-600 px-6 text-sm font-semibold text-white shadow-sm hover:from-violet-800 hover:to-purple-700 disabled:opacity-50"><UserPlus className="h-5 w-5" />{addingUser ? 'Adding...' : 'Add to team'}</button></div>
                  </div>

                  {addedUsers.length > 0 && <div><h2 className="mb-3 text-sm font-bold text-slate-900">Invited members ({addedUsers.length})</h2><div className="space-y-3">{addedUsers.map(user => <div key={user._id} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3"><span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-violet-100 font-bold text-violet-700">{user.name?.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase()}</span><div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-slate-900">{user.name}</p><p className="truncate text-sm text-slate-500">{user.email}</p><p className="mt-1 text-xs text-slate-500">Temporary password: <code className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-800">{user.temporaryPassword}</code></p></div><span className="rounded-full bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700">{user.roleName}</span><button type="button" onClick={() => removeAddedUser(user._id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label={`Remove ${user.name}`}><Trash2 className="h-5 w-5" /></button></div>)}</div></div>}
                </div>}

                {error && <p className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
              </div>

              <div className="mt-9 flex items-center justify-between border-t border-slate-200 pt-6">
                <button type="button" disabled={saving} onClick={() => finishOrAdvance({}, true)} className="text-sm font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50">Skip this step</button>
                <div className="flex gap-3">{step > 0 && <button type="button" disabled={saving} onClick={() => setStep(currentStep => currentStep - 1)} className="h-12 rounded-xl border border-slate-300 bg-white px-7 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50">Back</button>}<button type="button" disabled={saving || (step === 0 && !form.name.trim())} onClick={saveCurrentStep} className="flex h-12 items-center gap-3 rounded-xl bg-gradient-to-r from-violet-700 to-purple-600 px-7 text-sm font-semibold text-white shadow-sm hover:from-violet-800 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50">{saving ? 'Saving...' : step === steps.length - 1 ? 'Finish setup' : 'Continue'}{!saving && <ArrowRight className="h-5 w-5" />}</button></div>
              </div>
            </div>

            <aside className="hidden border-l border-slate-200 bg-gradient-to-b from-white to-violet-50/40 p-10 lg:flex lg:flex-col lg:justify-center">
              <div className="mx-auto mb-10 flex h-44 w-44 items-center justify-center rounded-full bg-violet-100/70 text-violet-700"><StepIcon className="h-20 w-20" strokeWidth={1.5} /></div>
              <h2 className="mb-6 text-2xl font-bold text-slate-950">{benefits.heading}</h2>
              <div className="space-y-6">{benefits.points.map(point => { const BenefitIcon = point.icon; return <div key={point.title} className="flex gap-4"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700"><BenefitIcon className="h-6 w-6" /></span><div><h3 className="font-semibold text-slate-900">{point.title}</h3><p className="mt-0.5 text-sm leading-5 text-slate-500">{point.text}</p></div></div>; })}</div>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}
